"use client";

import React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../../../lib/utils.js";

/**
 * Shared two-pane layout primitives for the calendar event create/edit/view
 * surfaces. On desktop (lg+) content splits into a wider primary column and a
 * narrower metadata sidebar so wide modals use their horizontal space instead
 * of stacking everything in one column. Stacks to a single column on mobile.
 *
 * Used by the create modals, the edit dialog, and the view modal so all three
 * read as siblings.
 */

type DivProps = {
  children: React.ReactNode;
  className?: string;
};

/** Outer responsive grid: 1 col on mobile, 5-col split on lg+ (3 / 2). */
export function EventFormGrid({ children, className }: DivProps) {
  return (
    <div className={cn("grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8", className)}>
      {children}
    </div>
  );
}

/** Wider left column (primary inputs / main content) — 3/5 on lg+. */
export function EventFormPrimaryColumn({ children, className }: DivProps) {
  return <div className={cn("space-y-6 lg:col-span-3", className)}>{children}</div>;
}

/** Narrower right column (metadata / secondary inputs) — 2/5 on lg+. */
export function EventFormSidebar({ children, className }: DivProps) {
  return <div className={cn("space-y-6 lg:col-span-2", className)}>{children}</div>;
}

interface EventFormSectionProps extends DivProps {
  title?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

/** A bordered, titled grouping for a set of related fields/facts. */
export function EventFormSection({
  title,
  description,
  icon: Icon,
  children,
  className,
}: EventFormSectionProps) {
  return (
    <section className={cn("space-y-4 rounded-lg border bg-card/50 p-4", className)}>
      {(title || description) && (
        <div className="space-y-0.5">
          {title && (
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
              {title}
            </h3>
          )}
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      {children}
    </section>
  );
}

interface EventFormCollapsibleSectionProps extends DivProps {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  defaultOpen?: boolean;
}

/**
 * Progressive disclosure: a titled section collapsed by default. Use for
 * advanced / optional fields so the common path stays uncluttered.
 */
export function EventFormCollapsibleSection({
  title,
  description,
  icon: Icon,
  defaultOpen = false,
  children,
  className,
}: EventFormCollapsibleSectionProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <section className={cn("rounded-lg border bg-card/50", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-lg p-4 text-left text-sm font-semibold text-foreground hover:bg-muted/40"
      >
        <span className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          {title}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div className="space-y-4 px-4 pb-4">
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {children}
        </div>
      )}
    </section>
  );
}
